import React from "react";
import { SimpleGrid } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import { type Banner, type Page as SanityPage, getPage, getAnnouncementBanner } from "lib/sanity";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Customers } from "components/logos";
import { Feature } from "components/feature";
import { DemoEmbedHalf } from "components/demo-embed";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import heroImage from "../../../public/assets/solutions/saas/hero.png";
import firstPairImage from "../../../public/assets/solutions/saas/pair-1.svg";
import secondPairImage from "../../../public/assets/solutions/saas/pair-2.svg";
import thirdPairImage from "../../../public/assets/solutions/saas/pair-3.svg";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
};

const SaaS: NextPage<Readonly<PageProps>> = ({ page, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      {page.customers && <Customers customers={page.customers} />}

      <Section center>
        <SimpleGrid columns={[1, 1, 2, 4]} width="100%" justifyItems="center" gap={20}>
          <Feature
            center
            title="Target"
            description="Target prospects based on how they use your product."
            image="/assets/solutions/saas/feature-1.svg"
          />
          <Feature
            center
            title="Personalize"
            description="Personalize your messages to how prospects use your product."
            image="/assets/solutions/saas/feature-2.svg"
          />
          <Feature
            center
            title="Get notified"
            description="Get notified when key customers take actions in your product."
            image="/assets/solutions/saas/feature-3.svg"
          />
          <Feature
            center
            title="Identify"
            description="Identify when customers use premium features."
            image="/assets/solutions/saas/feature-4.svg"
          />
        </SimpleGrid>
      </Section>

      <DemoEmbedHalf
        eyebrow="#1 Reverse ETL"
        title="Start syncing data in minutes"
        description="Your data is ready to use in your warehouse or BI tool. Use Hightouch to sync that data to sales and marketing tools without APIs, CSVs, or engineering favors."
      />

      <Section>
        <PairImage
          align="right"
          image={firstPairImage}
          eyebrow="Build a 360˚ customer view"
          title="Hyper-personalize  customer journeys"
          description="Your business teams shouldn’t have to open 10+ tabs to understand the customer journey. Bring the data that matters to the places your team already live - whether that’s a CRM like Salesforce or Hubspot, a CS platform like Gainsight, a feed in Slack, or even Airtable."
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={secondPairImage}
          eyebrow="Sales teams"
          title="Increase revenue with better data "
          description="Tap directly into your warehouse to prioritize leads based on all customer data across your touch points - no engineering favors required. Build PQL and MQL models that make sense for your business and sync them to your CRMs."
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={thirdPairImage}
          eyebrow="Marketing teams"
          title="Reach the right users at the right time"
          description="Run personalized, multi-channel campaigns using rich customer data from your CRM and product database. Use BI tools like Looker or jump straight to Hightouch Customer Studio to visually select the data that powers your campaigns."
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          author={{
            name: "William Tsu",
            job: "Customer Success Operations",
          }}
          company={{
            name: "Blend",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/eb8e724a9b5e0f1c6ba686fb37685d051030386e-176x73.svg",
          }}
          quoteText="The value isn’t one individual connector... it’s blending disparate data and pushing it [back] out so everyone’s looking at the same metrics."
          cta={{ href: "/customers/blend", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export default SaaS;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getPage("/solutions/saas"), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};
